import { memo, useRef, useState, useEffect } from "react";
import * as amplitude from "@amplitude/analytics-browser";
import { ChevronRight } from "@unified-checkout/design-system/icons/index";
import { Dropdown, DropdownMenu } from "@unified-checkout/design-system";
import useNavigateWithParams from "libs/design-system/src/hooks/useNavigateWithParams";
import { SHARED_EVENTS, SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import type { TNavbarSpecialActions } from "@app/dashboard/types/innerLayout/nav-links";
import type { INavItem } from "@app/dashboard/types/innerLayout/nav-item";
import { useNavigationStore } from "@app/dashboard/store/navigation";
import { useClickOutside } from "@app/dashboard/hooks/useClickOutside";
import { fireEventBeforeNavigation } from "@app/dashboard/utils/tracking/fireEventBeforeNavigation";
import { getCurrentPath } from "@app/dashboard/utils/getCurrentPath";
import SubItem from "./SubItem";
import SubItemsList from "./SubItemsList";
import styles from "./nav-item.module.scss";

export const NavItem = memo((
  {
    id,
    icon,
    label,
    active = false,
    disabled = false,
    route,
    subItems,
    hasSubLinks,
    collapsed,
    specialAction: navItemSpecialAction,
    selectable = true,
    tourElementId,
    onNavItemClick
  }
    : INavItem
) => {
  const navItemRef = useRef<any>();
  const subItemsRef = useRef<any>();
  const { setActiveLink, specialAction, activeLink } = useNavigationStore();
  const navigateWithParams = useNavigateWithParams();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const tourStartHandler = () => {
      const hasTourElement = subItems?.some(subItem => subItem.tourElementId);

      if (hasTourElement) {
        setIsOpen(true);
      }
    };

    const tourEndHandler = () => {
      const hasActiveSubItem = subItems?.some(subItem => subItem.active);
      setIsOpen(!!hasActiveSubItem);
    };

    window.addEventListener('joyride:tour:start', tourStartHandler as EventListener);
    window.addEventListener('joyride:tour:end', tourEndHandler as EventListener);

    return () => {
      window.removeEventListener('joyride:tour:start', tourStartHandler as EventListener);
      window.removeEventListener('joyride:tour:end', tourEndHandler as EventListener);
    };
  }, [subItems]);

  // Auto-expand if any sub-item is active
  useEffect(() => {
    if (subItems && subItems.length > 0 && !collapsed) {
      const hasActiveSubItem = subItems.some(subItem => subItem.active);
      if (hasActiveSubItem) {
        setIsOpen(true);
      }
    }
  }, [subItems, activeLink, collapsed]);

  useEffect(() => {
    collapsed && hasSubLinks && setIsOpen(false);
  }, [collapsed]);

  const dropdownToggle = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  const handleNavItemClick = async (
    label: string,
    hasSubLinks: boolean,
    route?: string,
    navItemSpecialAction?: TNavbarSpecialActions,
    id?: string,
  ) => {

    if (!hasSubLinks) {
      if (route) {
        selectable && setActiveLink({ nav: label, sub: "" });

        amplitude.track(SHARED_EVENTS.NAVIGATION_ITEM_CLICKED, {
          [SHARED_EVENTS_PARAMS.NAV_ITEM]: id || label,
          [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath(),
          [SHARED_EVENTS_PARAMS.TO_PAGE]: route,
          [SHARED_EVENTS_PARAMS.IS_COLLAPSED_NAV]: !!collapsed,
        });
        navigateWithParams(route);
        onNavItemClick?.();
      };
    };

    if (navItemSpecialAction) {
      await fireEventBeforeNavigation(
        () => amplitude.track(SHARED_EVENTS.NAVIGATION_ITEM_CLICKED, {
          [SHARED_EVENTS_PARAMS.NAV_ITEM]: id || label,
          [SHARED_EVENTS_PARAMS.FROM_PAGE]: getCurrentPath(),
          [SHARED_EVENTS_PARAMS.IS_COLLAPSED_NAV]: !!collapsed,
        })
      );

      specialAction?.(navItemSpecialAction);
    };

    dropdownToggle();
  };

  const navItemBase = <>
    <div className={styles["nav-item__featured-icon"]}>{icon}</div>

    {label && !collapsed && <div className={styles["nav-item__label"]}>{label}</div>}

    {
      (hasSubLinks && !collapsed) &&
      <div className={styles["nav-item__chevron"]}>
        {ChevronRight}
      </div>
    }
  </>;

  const hasActiveSubItem = subItems?.some(subItem => subItem.active) ?? false;
  const isNavItemActive = !hasSubLinks ? active : (hasActiveSubItem || active);

  const NavItemInstance = <div className={styles["nav-item"]}
    id={tourElementId}
    data-nav-item-active={isNavItemActive}
    data-nav-item-disabled={disabled}
    data-nav-item-open={isOpen}
    data-nav-item-has-sublinks={hasSubLinks}
    data-nav-item-collapsed={collapsed}
    tabIndex={0}
    ref={navItemRef}
    onClick={() => handleNavItemClick(label, !!subItems, route, navItemSpecialAction, id)}
  >
    {
      route && selectable && !hasSubLinks ?
        <a href={`${window.location.origin}${route}`} onClick={(e) => e.preventDefault()}>
          {navItemBase}
        </a>
        :
        navItemBase
    }
  </div>;

  useClickOutside([navItemRef, subItemsRef], closeDropdown, isOpen);

  return (
    <>
      {
        collapsed ?
          (
            subItems ?
              <Dropdown closeOnClickOutside={false} width="fit-content">
                {NavItemInstance}
                <DropdownMenu
                  open={isOpen}
                  width="fit-content"
                  horizontalPlacement="start"
                  verticalPlacement='auto'
                >
                  <SubItemsList
                    ref={subItemsRef}
                    title={label}
                    subItems={subItems}
                    navItemId={id}
                    closeDropdown={closeDropdown}
                    onSubItemClick={onNavItemClick}
                    collapsed={collapsed}
                  />
                </DropdownMenu>
              </Dropdown>
              :
              NavItemInstance
          )
          :
          NavItemInstance
      }

      {
        isOpen && !collapsed && subItems?.map((_, index: number) => {
          return (
            <SubItem
              key={`subitem-${index}`}
              subItems={subItems}
              index={index}
              navItemLabel={label}
              navItemId={id}
              onSubItemClick={onNavItemClick}
              collapsed={collapsed}
            />
          )
        })
      }
    </>
  )
});